import React, { useEffect, useMemo, useState } from 'react';
import { BookedLisitng, BookingDetail, BookingSuccess, EmptyBooking, RefundModal } from './components';
import { notify } from '../../components';
import { getBookingById, getBookingsList } from './api';
import { useSelector } from 'react-redux';
import { addMilliseconds, isPast } from 'date-fns';
import { TIME_INTERVAL_CONFIG } from '../../constants/timeIntervalConfig';
import SkeletonLoader from '../../components/loaders/skeletonLoader';

const Booking = () => {
  const [bookingData, setBookingData] = useState(null);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const { bookings } = useSelector((state) => state.bookingsList);
  const { activeRequests } = useSelector((state) => state.app);

  useEffect(() => {
    getBookingsList();
  }, []);

  useEffect(() => {
    if (activeRequests?.includes('get-bookings-list')) {
      setLoadingBookings(true);
    } else {
      setLoadingBookings(false);
    }
  }, [activeRequests]);

  const onListingClick = (booking) => {
    getBookingById(booking?.id, (data) => {
      setBookingData(data);
    });
  };

  const onBackClick = () => {
    setBookingData(null);
  };

  const handleRefundClick = (eventItem) => {
    setBookingData(eventItem);
    setIsRefundModalOpen(true);
  };

  const handleRefundSave = (formData, eventId) => {
    setIsRefundModalOpen(false);
    notify('success', 'Refund initiated successfully!', 'bottom-center', '2000');
  };

  const { upcomingBookings, pastBookings } = useMemo(() => {
    const upcomingBookings = [];
    const pastBookings = [];

    bookings?.forEach((booking) => {
      if (booking?.is_active && !isPast(addMilliseconds(new Date(booking?.end_date), TIME_INTERVAL_CONFIG.BOOKINGS_END))) upcomingBookings.push(booking);
      else pastBookings.push(booking);
    });

    return {
      upcomingBookings,
      pastBookings,
    };
  }, [bookings]);

  // Show loader only if we're loading AND don't have any existing data
  const shouldShowLoader = loadingBookings && !bookings?.length;

  return (
    <div className="user-listing-container w-[100%] md:items-center m-auto mt-[72px] md:mt-[100px] bg-grey-5 py-0 md:py-5">
      <div className="flex flex-col gap-9 m-auto">
        {bookingData ? (
          <BookingDetail 
          {...bookingData}
          onAllTicketsClick={onBackClick}
          onRefundClick={() => handleRefundClick(bookingData)} 
        />
        ) : (
          <div className={`${window.innerWidth < 650 ? 'w-full':'w-[640px]'} p-6 md:p-0  flex flex-col gap-9 m-auto`}>
            <div className="heading-36 md:pt-6">My Bookings</div>
            <div>
              {!bookings?.length ? (
                shouldShowLoader ? (
                  <SkeletonLoader type="list" count={3} listClassName='h-[100px]' />
                ) : (
                  <EmptyBooking />
                )
              ) : (
                <div className='flex flex-col gap-4'>
                  <>
                    {!!upcomingBookings?.length && (
                      <>
                        <div className="heading-24 mb-4">Upcoming Bookings</div>
                        {upcomingBookings?.map((eventItem, index) => (
                          <BookedLisitng 
                            key={index} 
                            {...eventItem} 
                            statusTags={{ label: 'BOOKED' }}
                            onRefundClick={() => handleRefundClick(eventItem)} 
                            onListingClick={() => onListingClick(eventItem)} 
                          />
                        ))}
                      </>
                    )}
                    {!!pastBookings?.length && (
                      <>
                        <div className="heading-24 mt-8 mb-4">Past Bookings</div>
                        {pastBookings?.map((eventItem, index) => (
                          <BookedLisitng 
                            key={index} 
                            {...eventItem}
                            statusTags={{ label: 'PAST EVENT' }}
                            onRefundClick={() => handleRefundClick(eventItem)} 
                            onListingClick={() => onListingClick(eventItem)} 
                          />
                        ))}
                      </>
                    )}
                  </>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {isRefundModalOpen && (
        <RefundModal
          isOpen={isRefundModalOpen}
          onClose={() => setIsRefundModalOpen(false)}
          onSave={(formData) => handleRefundSave(formData, bookingData.id)} 
          payout={null}  
        />
      )}
    </div>
  );
};

export default Booking;